<lib-sliding-side-view
  [isOpen]="isOpen()"
  [title]="company()?.companyProfile?.name || 'Company Details'"
  [width]="'380px'"
  (handleClose)="onClose()"
>
  @if (loading()) {
    <div class="loading-state">
      <p>{{ 'COMMON.LOADING' | translate }}</p>
    </div>
  }

  @if (error()) {
    <div class="error-state">
      <p class="error-message">{{ error() }}</p>
    </div>
  }

  @if (!loading() && !error() && company()) {
    <div class="side-view-content">
      <div class="section">
        <div class="field">
          <label>{{ 'country' | translate }}</label>
          <div class="value country-value">
            @if (getFlagUrl(company()!.companyProfile.countryIso2)) {
              <img
                [src]="getFlagUrl(company()!.companyProfile.countryIso2)"
                [alt]="company()!.companyProfile.countryIso2"
                class="country-flag"
              />
            }
            <span>{{ company()!.companyProfile.countryIso2 }}</span>
          </div>
        </div>
      </div>

      @if (company()!.did) {
        <div class="section">
          <div class="field">
            <div class="label-with-action">
              <label>{{ 'did' | translate }}</label>
              <lib-tooltip [tooltip]="'copy' | translate">
                <lib-icon-button
                  icon="copy"
                  (clicked)="copyToClipboard(company()!.did, 'DID')"
                  [size]="18"
                  [noBackground]="true"
                ></lib-icon-button>
              </lib-tooltip>
              @if (didDocument()) {
                <lib-tooltip [tooltip]="'view-did-document' | translate">
                  <lib-icon-button
                    icon="eye"
                    (clicked)="openDidModal()"
                    [size]="18"
                    [noBackground]="true"
                  ></lib-icon-button>
                </lib-tooltip>
              }
            </div>
            <div class="value monospace">{{ company()!.did }}</div>
          </div>
        </div>
      }

      <!-- Credentials Section (unified: issued + pending) -->
      @if (unifiedCredentials().length > 0) {
        <lib-card
          class="credentials-card"
          variant="tertiary"
          [title]="('credentials' | translate) + ' (' + unifiedCredentials().length + ')'"
        >
          <div class="credentials-list">
            @for (credential of unifiedCredentials(); track credential.type) {
              <div class="credential-item">
                <div class="credential-header">
                  <div class="credential-info">
                    <span class="credential-type">{{ credential.label }}</span>
                    @if (credential.isIssued) {
                      <lib-tooltip [tooltip]="'view-json' | translate">
                        <lib-icon-button
                          icon="file-text"
                          [size]="20"
                          (clicked)="openCredentialModal(credential.rawCredential)"
                        />
                      </lib-tooltip>
                    }
                  </div>
                  <lib-badge
                    [label]="getCredentialStatusLabel(credential.status)"
                    [colorScheme]="getCredentialStatusBadgeColor(credential.status)"
                  />
                </div>
                @if (credential.isIssued) {
                  <div class="credential-footer">
                    <div class="credential-details">
                      @if (credential.status === 'revoked') {
                        <div class="credential-detail-row">
                          <span class="detail-label">{{ 'revoked-on' | translate }}:</span>
                          <span class="detail-value fade revoked">{{ formatDate(credential.revokedAt!) }}</span>
                        </div>
                      } @else {
                        <div class="credential-detail-row clickable" (click)="toggleDateDisplay('issued')">
                          @if (issuedDateMode() === 'absolute') {
                            <span class="detail-label">{{ 'issued' | translate }}:</span>
                            <span class="detail-value fade">{{ formatDate(credential.issuedAt!) }}</span>
                          } @else {
                            <span class="detail-label">{{ 'issued-since' | translate }}:</span>
                            <span class="detail-value fade">{{ getDaysSince(credential.issuedAt!) }}</span>
                          }
                        </div>
                        @if (credential.expiresAt) {
                          <div class="credential-detail-row clickable" (click)="toggleDateDisplay('expires')">
                            @if (expiresDateMode() === 'relative') {
                              <span class="detail-label">{{ 'expires-in' | translate }}:</span>
                              <span class="detail-value fade" [class.expired]="credential.status === 'expired'">{{ getDaysRemaining(credential.expiresAt) }}</span>
                            } @else {
                              <span class="detail-label">{{ 'valid-until' | translate }}:</span>
                              <span class="detail-value fade" [class.expired]="credential.status === 'expired'">{{ formatDate(credential.expiresAt) }}</span>
                            }
                          </div>
                        }
                      }
                    </div>
                    @if (canRevokeCredential(credential)) {
                      <lib-button
                        variant="text"
                        size="small"
                        color="danger"
                        icon="x-circle"
                        (clicked)="requestRevokeCredential(credential.rawCredential)"
                      >
                        {{ 'revoke' | translate }}
                      </lib-button>
                    }
                  </div>
                }
              </div>
            }
          </div>
        </lib-card>
      }

    </div>
  }
</lib-sliding-side-view>

<!-- DID Document Modal -->
<lib-did-document-modal
  [isOpen]="isDidModalOpen()"
  [didDocument]="didDocument()"
  (handleClose)="closeDidModal()"
/>

<!-- Credential JSON Modal -->
<app-credential-json-modal
  [isOpen]="isCredentialModalOpen()"
  [credential]="selectedCredential()"
  (close)="closeCredentialModal()"
/>
